<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { ArrowRight, Mouse } from 'lucide-vue-next';
import { login } from '@/routes';
import HeroPipeline from './HeroPipeline.vue';
</script>

<template>
    <section
        class="relative flex min-h-[calc(100svh-5.5rem)] flex-col justify-center overflow-hidden bg-white pt-8 pb-24 dark:bg-neutral-950"
    >
        <!-- Subtle ambient glow -->
        <div class="pointer-events-none absolute inset-0">
            <div
                class="absolute top-1/4 left-1/2 h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-brand/[0.04] blur-3xl"
            ></div>
        </div>

        <div class="relative z-10 mx-auto max-w-5xl px-4">
            <!-- Centered content -->
            <div class="flex flex-col items-center text-center">
                <span
                    class="mb-6 inline-flex items-center rounded-full border border-border/60 px-4 py-1.5 text-center text-xs font-medium text-muted-foreground backdrop-blur-sm"
                >
                    Master recherche · Doctorants · Chercheurs
                </span>

                <h1
                    class="max-w-3xl text-3xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-6xl"
                >
                    Arrêtez de jongler entre
                    <span class="text-brand">Zotero, Excel et Word</span> pour
                    votre revue de littérature
                </h1>

                <p
                    class="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg"
                >
                    Rewio réunit l'import de vos références, le tri de vos
                    articles, l'extraction de vos données et la rédaction de
                    votre revue de littérature — au même endroit. Vous gardez le
                    fil et vous gagnez du temps.
                </p>

                <div
                    class="mt-10 flex flex-wrap items-center justify-center gap-4"
                >
                    <Link
                        :href="login()"
                        class="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-2.5 text-sm font-medium text-white transition-all hover:bg-brand/85"
                    >
                        Commencer gratuitement
                        <ArrowRight class="h-4 w-4" />
                    </Link>

                    <a
                        href="#features"
                        class="inline-flex items-center gap-2 rounded-full border border-border/60 px-6 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent/60"
                    >
                        Voir comment ça marche
                    </a>
                </div>

                <p class="mt-4 text-xs font-medium text-muted-foreground">
                    Gratuit pour commencer · Sans carte bancaire
                </p>

                <!-- Social proof -->
                <div class="mt-8 flex items-center justify-center gap-3">
                    <div class="flex -space-x-2">
                        <div
                            v-for="i in 4"
                            :key="i"
                            class="flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-brand/20 text-[10px] font-semibold text-brand dark:border-neutral-950"
                        >
                            {{ ['M', 'T', 'S', 'A'][i - 1] }}
                        </div>
                    </div>
                    <p class="text-xs text-muted-foreground">
                        <span class="font-semibold text-foreground">850+</span>
                        chercheurs font déjà confiance à Rewio
                    </p>
                </div>
            </div>

            <!-- Compact animated pipeline -->
            <div class="mt-14">
                <HeroPipeline />
            </div>
        </div>

        <!-- Scroll indicator -->
        <a
            href="#features"
            aria-label="Défiler pour découvrir"
            class="absolute bottom-6 left-1/2 -translate-x-1/2 text-muted-foreground transition-colors hover:text-foreground"
        >
            <Mouse class="h-6 w-6 motion-safe:animate-bounce" />
        </a>
    </section>
</template>
